import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Pencil, Trash2, Eye, EyeOff, AlertTriangle, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { getTalentTypeLabel, getTalentTypeColor, getStatusTypes, DEPARTURE_KIND_LABELS } from '../../utils/talentTypeUtils';
import StatusForm from './StatusForm';
import ConfirmDialog from '../UI/ConfirmDialog';

const StatusCard = ({
  status,
  stages = [],
  onUpdate,
  onDelete,
  onViewTransitions,
  isEditing = false,
  onEdit,
  onCancelEdit
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const talentsCount = status.talents_count || 0;

  const handleEdit = () => {
    if (onEdit) {
      onEdit(status.id);
    }
  };

  const handleDelete = () => {
    setDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete(status.id);
    } catch (error) {
      console.error('Error deleting status:', error);
      toast.error('Error deleting status: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdate = async (updateData) => {
    try {
      await onUpdate(status.id, updateData);
      // setIsEditing(false) больше не нужен - это делает родительский компонент
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Error updating status: ' + error.message);
    }
  };

  const handleCancel = () => {
    if (onCancelEdit) {
      onCancelEdit();
    }
  };

  if (isEditing) {
    return (
      <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-md border border-light-border-primary dark:border-dark-border-primary p-6">
        <StatusForm
          initialData={status}
          onSubmit={handleUpdate}
          onCancel={handleCancel}
          submitLabel="Save"
        />
      </div>
    );
  }

  return (
    <div className="bg-light-bg-primary dark:bg-dark-bg-secondary rounded-lg shadow-md border border-light-border-primary dark:border-dark-border-primary p-6 hover:shadow-lg transition-shadow duration-200">
      {/* Header with title and action buttons */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div
            className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0"
            style={{ backgroundColor: status.color || '#6B7280' }}
          ></div>
          <h3 className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary break-words leading-tight hyphens-auto flex-1 min-w-0" title={status.name} style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>{status.name}</h3>
        </div>

        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={handleEdit}
            className="p-2 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors duration-150"
            title="Edit (description and settings only)"
          >
            <Pencil className="w-4 h-4" />
          </button>

          <button
            onClick={() => onViewTransitions(status)}
            className="p-2 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors duration-150"
            title="Transitions"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>

          <button
            onClick={handleDelete}
            disabled={isDeleting || talentsCount > 0}
            className="p-2 text-light-text-tertiary dark:text-dark-text-tertiary hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-light-text-tertiary dark:disabled:hover:text-dark-text-tertiary duration-150"
            title={talentsCount > 0
              ? `Cannot delete: ${talentsCount} talent(s) have this status. Move them to another status first.`
              : 'Delete'}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content section */}
      <div className="space-y-3">
        {/* Тип: у статуса таланта своего типа нет — показываем типы стадий,
            к которым он привязан */}
        <div>
          <div className="text-[10px] font-medium uppercase tracking-wide text-light-text-tertiary dark:text-dark-text-tertiary mb-1">
            {status.talent_type === 'talent' ? 'Talent type · derived from stages' : 'Status type'}
          </div>
          <div className="flex flex-wrap items-center gap-2">
          {getStatusTypes(status).map((type) => (
            <span
              key={type}
              className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getTalentTypeColor(type)}`}
              title={status.talent_type === 'talent'
                ? (type === 'talent'
                  ? 'This status is not linked to any stage yet, so it has no talent type'
                  : `Not stored on the status: it is linked to at least one ${getTalentTypeLabel(type)} stage. Change it in Stage settings.`)
                : undefined}
            >
              <span className="w-2 h-2 rounded-full bg-current mr-2"></span>
              {getTalentTypeLabel(type)}
            </span>
          ))}
          {status.departure_kind && (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"
              title="Set automatically when a matching important date comes"
            >
              Departure: {DEPARTURE_KIND_LABELS[status.departure_kind] || status.departure_kind}
            </span>
          )}
          </div>
        </div>

        {/* Stages — отдельная ось от статуса. Без этого блока со страницы
            статусов нельзя было увидеть привязку вообще: обнаружить статус
            без единой стадии можно было только вручную перебрав модалки на
            отдельной странице Stages. Подпись обязательна: без неё ряд цветных
            чипов неотличим от любого другого списка бейджей на карточке. */}
        <div>
          <div className="text-[10px] font-medium uppercase tracking-wide text-light-text-tertiary dark:text-dark-text-tertiary mb-1">
            Stages
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
          {stages.length === 0 ? (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400"
              title="This status isn't linked to any stage — it won't show up as an option in the Status dropdown after a stage is selected"
            >
              <AlertTriangle className="w-3 h-3" />
              Not linked to any stage
            </span>
          ) : (
            stages.map((stage) => (
              <span
                key={stage.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary"
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: stage.color || '#6B7280' }} />
                {stage.name}
              </span>
            ))
          )}
          </div>
        </div>

        {status.description && (
          <p className="text-light-text-secondary dark:text-dark-text-secondary text-sm line-clamp-2 break-words">{status.description}</p>
        )}

        <div className="flex items-center gap-4 text-sm text-light-text-tertiary dark:text-dark-text-tertiary">
          <div className="flex items-center gap-1">
            {status.is_active ? (
              <span className="text-green-600 font-medium">Active</span>
            ) : (
              <span className="text-red-600 font-medium">Inactive</span>
            )}
          </div>

          <div className="flex items-center gap-1">
            {status.public_visible ? (
              <>
                <Eye className="w-4 h-4 text-green-600" />
                <span className="text-green-600">Public</span>
              </>
            ) : (
              <>
                <EyeOff className="w-4 h-4 text-gray-400" />
                <span className="text-light-text-tertiary dark:text-dark-text-tertiary">Hidden</span>
              </>
            )}
          </div>

          {status.display_order && (
            <span>Order: {status.display_order}</span>
          )}

          {status.talent_type === 'talent' && status.talents_count !== undefined && (
            talentsCount > 0 ? (
              <Link
                to={`/talents?status_id=${status.id}`}
                className="flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline"
                title="Show talents with this status"
              >
                <Users className="w-4 h-4" />
                Talents: {talentsCount}
              </Link>
            ) : (
              <span className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                Talents: 0
              </span>
            )
          )}
        </div>

        {/* Library Status ID - more prominent */}
        <div>
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary">
            <span className="w-1.5 h-1.5 rounded-full bg-light-text-tertiary dark:bg-dark-text-tertiary mr-1.5"></span>
            Library ID: {status.library_status_id || 'Not specified'}
          </span>
        </div>
      </div>
      <ConfirmDialog
        isOpen={deleteConfirm}
        onClose={() => setDeleteConfirm(false)}
        onConfirm={confirmDelete}
        confirmStyle="danger"
        title="Delete Status"
        message={`Are you sure you want to delete the status "${status.name}"? This action cannot be undone.`}
        confirmText="Delete"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default StatusCard;
